// =====================
// US holiday presets + floating-date resolution
// =====================
//
// Single source of truth for the "Suggested holidays" feature and for
// recomputing floating holidays each year. Used by both the API (persisting
// the rule, normalizing returned dates) and the web UI (rendering suggestions).
//
// Two kinds of holidays:
//   - fixed:    same month/day every year (e.g. Christmas 12/25). Stored with
//               `isRecurring: true` and no `recurrenceRule`; the existing
//               setFullYear() expansion is correct.
//   - floating: an "nth weekday of month" rule (e.g. Thanksgiving = 4th
//               Thursday of November). Stored with a `recurrenceRule` string so
//               the actual date can be recomputed per year instead of drifting.
//
// Easter-based holidays (e.g. Good Friday) are intentionally excluded — they
// require the computus algorithm; add later if product asks.

export type HolidayKind = 'fixed' | 'floating'

export interface UsHolidayPreset {
  /** Stable identifier (not persisted; used for de-duping in the UI). */
  key: string
  /** Display name, also used to match against already-added holidays. */
  name: string
  kind: HolidayKind
  /** 1-12. */
  month: number
  /** Fixed holidays: day of month (1-31). */
  day?: number
  /** Floating holidays: 1-4 for nth week, or -1 for the last week. */
  week?: number
  /** Floating holidays: 0=Sunday … 6=Saturday. */
  weekday?: number
  /** Optional day offset applied after resolving (e.g. day after Thanksgiving). */
  offsetDays?: number
  /** True for non-federal but commonly-observed holidays. */
  isCommon?: boolean
}

// The 11 US federal holidays plus a few commonly-observed extras.
export const US_HOLIDAY_PRESETS: UsHolidayPreset[] = [
  // --- Federal, fixed date ---
  { key: 'new-years-day', name: "New Year's Day", kind: 'fixed', month: 1, day: 1 },
  { key: 'juneteenth', name: 'Juneteenth', kind: 'fixed', month: 6, day: 19 },
  { key: 'independence-day', name: 'Independence Day', kind: 'fixed', month: 7, day: 4 },
  { key: 'veterans-day', name: 'Veterans Day', kind: 'fixed', month: 11, day: 11 },
  { key: 'christmas-day', name: 'Christmas Day', kind: 'fixed', month: 12, day: 25 },
  // --- Federal, floating (nth weekday of month) ---
  { key: 'mlk-day', name: 'Martin Luther King Jr. Day', kind: 'floating', month: 1, week: 3, weekday: 1 },
  { key: 'presidents-day', name: "Presidents' Day", kind: 'floating', month: 2, week: 3, weekday: 1 },
  { key: 'memorial-day', name: 'Memorial Day', kind: 'floating', month: 5, week: -1, weekday: 1 },
  { key: 'labor-day', name: 'Labor Day', kind: 'floating', month: 9, week: 1, weekday: 1 },
  { key: 'columbus-day', name: 'Columbus Day', kind: 'floating', month: 10, week: 2, weekday: 1 },
  { key: 'thanksgiving', name: 'Thanksgiving Day', kind: 'floating', month: 11, week: 4, weekday: 4 },
  // --- Common extras ---
  { key: 'day-after-thanksgiving', name: 'Day after Thanksgiving', kind: 'floating', month: 11, week: 4, weekday: 4, offsetDays: 1, isCommon: true },
  { key: 'christmas-eve', name: 'Christmas Eve', kind: 'fixed', month: 12, day: 24, isCommon: true },
  { key: 'new-years-eve', name: "New Year's Eve", kind: 'fixed', month: 12, day: 31, isCommon: true },
]

/**
 * Encode a floating-holiday rule as a compact string.
 * Format: `nth:<month>:<week>:<weekday>[:+offsetDays]`
 */
export function encodeRecurrenceRule(preset: UsHolidayPreset): string {
  const base = `nth:${preset.month}:${preset.week}:${preset.weekday}`
  return preset.offsetDays ? `${base}:${preset.offsetDays}` : base
}

interface ParsedRule {
  month: number
  week: number
  weekday: number
  offsetDays: number
}

/** Parse an `nth:...` rule string. Returns null if malformed or unrecognized. */
export function parseRecurrenceRule(rule: string | null | undefined): ParsedRule | null {
  if (!rule) return null
  const parts = rule.split(':')
  if (parts[0] !== 'nth' || parts.length < 4) return null
  const month = Number(parts[1])
  const week = Number(parts[2])
  const weekday = Number(parts[3])
  const offsetDays = parts[4] !== undefined ? Number(parts[4]) : 0
  if ([month, week, weekday, offsetDays].some(n => Number.isNaN(n))) return null
  return { month, week, weekday, offsetDays }
}

/**
 * Resolve a floating-holiday rule to an actual date for a given year.
 * `week` is 1-4 for the nth occurrence, or -1 for the last occurrence.
 * Returned Date is local noon to avoid timezone day-shift.
 */
export function resolveFloatingDate(
  month: number,
  week: number,
  weekday: number,
  year: number,
  offsetDays = 0,
): Date {
  let day: number
  if (week === -1) {
    // Last <weekday> of the month: walk back from the last day.
    const lastDay = new Date(year, month, 0).getDate() // day 0 of next month
    const lastDate = new Date(year, month - 1, lastDay)
    const diff = (lastDate.getDay() - weekday + 7) % 7
    day = lastDay - diff
  } else {
    // Nth <weekday>: find the first matching weekday, then add whole weeks.
    const first = new Date(year, month - 1, 1)
    const firstMatch = 1 + ((weekday - first.getDay() + 7) % 7)
    day = firstMatch + (week - 1) * 7
  }
  return new Date(year, month - 1, day + offsetDays, 12, 0, 0)
}

/** Resolve any encoded `nth:...` rule string for a year. Null if unparseable. */
export function resolveHolidayDate(rule: string, year: number): Date | null {
  const parsed = parseRecurrenceRule(rule)
  if (!parsed) return null
  return resolveFloatingDate(parsed.month, parsed.week, parsed.weekday, year, parsed.offsetDays)
}

/** Fixed-holiday date at local noon. */
function fixedDate(month: number, day: number, year: number): Date {
  return new Date(year, month - 1, day, 12, 0, 0)
}

/**
 * Effective date of a stored holiday for a given year.
 * Floating holidays (with a recurrenceRule) recompute; everything else keeps
 * its stored month/day (applied to the requested year for recurring ones).
 */
export function resolveHolidayForYear(
  holiday: { date: Date | string; recurrenceRule?: string | null; isRecurring?: boolean },
  year: number,
): Date {
  const fromRule = holiday.recurrenceRule ? resolveHolidayDate(holiday.recurrenceRule, year) : null
  if (fromRule) return fromRule
  const base = holiday.date instanceof Date ? holiday.date : new Date(holiday.date)
  if (holiday.isRecurring) {
    const shifted = new Date(base)
    shifted.setFullYear(year)
    return shifted
  }
  return base
}

export interface HolidayCreateInput {
  name: string
  date: Date
  isRecurring: boolean
  recurrenceRule: string | null
}

/**
 * Build the create payload for a preset in a given year. Fixed presets get a
 * concrete date + no rule; floating presets get this year's computed date as an
 * anchor plus the encoded rule that drives per-year recomputation.
 */
export function presetToCreateInput(preset: UsHolidayPreset, year: number): HolidayCreateInput {
  if (preset.kind === 'floating') {
    const date = resolveFloatingDate(
      preset.month,
      preset.week ?? 1,
      preset.weekday ?? 0,
      year,
      preset.offsetDays ?? 0,
    )
    return { name: preset.name, date, isRecurring: true, recurrenceRule: encodeRecurrenceRule(preset) }
  }
  return {
    name: preset.name,
    date: fixedDate(preset.month, preset.day ?? 1, year),
    isRecurring: true,
    recurrenceRule: null,
  }
}
